import { useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "@/client/hooks/use-toast";
import { isLocalHost } from "@/utils/envHelper";
import useAccountMessages from "@/utils/useAccountMessages";

interface RecaptchaExecuteParams {
    data: any;
    onSuccess: (data: any) => void;
    onError?: () => void;
    setPending?: (value: boolean) => void;
}

export default function useRecaptcha() {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const { getValMsg } = useAccountMessages();

    async function executeRecaptcha({ data, onSuccess, onError, setPending }: RecaptchaExecuteParams) {
        try {
            if (isLocalHost()) {
                await onSuccess({
                    ...data,
                    recaptchaToken: "development-bypass-token",
                });
                return;
            }

            if (!recaptchaRef.current) {
                throw new Error("ReCAPTCHA not initialized");
            }

            // Create a promise that can be rejected when the popup is closed
            const recaptchaPromise = new Promise<string | null>((resolve, reject) => {
                let resolved = false;

                // Add event listener to detect when focus returns to window
                const handleFocus = () => {
                    // Only proceed if we haven't resolved yet
                    if (resolved) return;

                    setTimeout(() => {
                        if (!resolved) {
                            resolved = true;
                            // Use resolve with null instead of reject to avoid unhandled promise rejection
                            resolve(null);
                        }
                    }, 500);
                };

                window.addEventListener("focus", handleFocus);

                // Call the original method
                recaptchaRef.current!.executeAsync()
                    .then((token) => {
                        resolved = true;
                        window.removeEventListener("focus", handleFocus);
                        resolve(token);
                    })
                    .catch((error) => {
                        resolved = true;
                        window.removeEventListener("focus", handleFocus);
                        resolve(null);
                    });
            });

            const token = await recaptchaPromise;

            if (!token) {
                throw new Error("ReCAPTCHA verification failed");
            }

            await onSuccess({ ...data, recaptchaToken: token });
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
        } catch (error) {
            console.error("ReCAPTCHA error:", error);
            toast({
                variant: "destructive",
                description: getValMsg("recaptcha-verification-failed"),
            });
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
            setPending?.(false);

            if (onError) {
                onError();
            }
        }
    }
    return { recaptchaRef, executeRecaptcha };
}
